import {
	questionnaire,
	type QuestionnaireAnswers,
	type QuestionnaireAnswerValue,
	type QuestionnaireQuestion,
	type QuestionnaireTemplate,
} from "@optimist/workflow-engine"
import { z } from "zod"

export const QUESTIONNAIRE_FREE_TEXT_MAX_LENGTH = 10_000

export const QuestionnaireQuestionTypeSchema = z.enum(questionnaire.questionTypes).meta({
	id: "QuestionnaireQuestionType",
})

export const QuestionnairePhaseSchema = z.enum(questionnaire.phases).meta({
	id: "QuestionnairePhase",
})

export const QuestionnaireGenderSchema = z.enum(questionnaire.genders).meta({
	id: "QuestionnaireGender",
})

export const QuestionnaireRiskLevelSchema = z.enum(questionnaire.riskLevels).meta({
	id: "QuestionnaireRiskLevel",
})

export const QuestionnaireSourceSchema = z
	.strictObject({
		pdf: z.string().min(1),
		page: z.number().int().positive(),
		section: z.string().min(1),
		questionNumber: z.string().min(1),
	})
	.meta({ id: "QuestionnaireSource" })

export const QuestionnaireAnswerMetadataSchema = z
	.strictObject({
		riskLevel: QuestionnaireRiskLevelSchema,
		source: QuestionnaireSourceSchema,
		disqualifies: z.boolean().optional(),
		responseKind: z.enum(["affirmative", "negative", "neutral"]).optional(),
		exclusive: z.boolean().optional(),
		displayText: z.string().min(1).optional(),
		aggregateItemText: z.string().min(1).optional(),
	})
	.meta({ id: "QuestionnaireAnswerMetadata" })

const QuestionnaireQuestionPresentationSchema = z
	.strictObject({
		headingTitle: z.string().min(1).optional(),
		headingSubtitle: z.string().min(1).optional(),
		detailsPromptLabel: z.string().min(1).optional(),
		inputControl: z.enum(["default", "medication_list"]).optional(),
		followUpQuestion: z.string().min(1).optional(),
	})

export const QuestionnaireStructuredAnswerSchema = z
	.strictObject({
		value: z.union([z.string(), z.array(z.string())]),
		details: z.string().optional(),
	})
	.meta({ id: "QuestionnaireStructuredAnswer" })

export const QuestionnaireAnswerValueSchema: z.ZodType<QuestionnaireAnswerValue> = z
	.union([z.string(), z.array(z.string()), QuestionnaireStructuredAnswerSchema])
	.meta({ id: "QuestionnaireAnswerValue" })

export const QuestionnaireAnswersSchema: z.ZodType<QuestionnaireAnswers> = z
	.record(z.string(), QuestionnaireAnswerValueSchema)
	.meta({ id: "QuestionnaireAnswers" })

export const QuestionnaireQuestionSchema: z.ZodType<QuestionnaireQuestion> = z
	.strictObject({
		id: z.string().min(1),
		question: z.string().min(1),
		subtitle: z.string().optional(),
		type: QuestionnaireQuestionTypeSchema,
		options: z.array(z.string().min(1)),
		phase: QuestionnairePhaseSchema,
		gender: QuestionnaireGenderSchema,
		riskLevel: QuestionnaireRiskLevelSchema,
		source: QuestionnaireSourceSchema,
		answerMetadata: z.record(z.string(), QuestionnaireAnswerMetadataSchema).optional(),
		disqualifyingAnswers: z.array(z.string().min(1)).optional(),
		detailsPrompt: z.string().min(1).optional(),
		detailsRequiredForAnswers: z.array(z.string().min(1)).optional(),
		presentation: QuestionnaireQuestionPresentationSchema.optional(),
	})
	.meta({ id: "QuestionnaireQuestion" })

export const QuestionnaireTemplateSchema: z.ZodType<QuestionnaireTemplate> = z
	.strictObject({
		id: z.enum(questionnaire.templateIds),
		version: z.string().min(1),
		questions: z.array(QuestionnaireQuestionSchema),
	})
	.meta({ id: "QuestionnaireTemplate" })

export const QuestionnaireTemplateResponseSchema = QuestionnaireTemplateSchema.meta({
	id: "QuestionnaireTemplateResponse",
})

export const QuestionnaireTemplatesResponseSchema = z
	.array(QuestionnaireTemplateResponseSchema)
	.meta({ id: "QuestionnaireTemplatesResponse" })

export const QuestionnaireTemplateIdSchema = z.enum(questionnaire.templateIds).meta({
	id: "QuestionnaireTemplateId",
})
